import * as React from "react";

export function useSize<T extends HTMLElement>() {
  const nodeRef = React.useRef<T | null>(null);
  const [width, setWidth] = React.useState(0);
  const [height, setHeight] = React.useState(0);

  const measure = React.useCallback((el: HTMLElement | null) => {
    if (!el) return;

    const r = el.getBoundingClientRect();
    const w = Math.max(0, Math.round(r.width));
    const h = Math.max(0, Math.round(r.height));
    setWidth(w);
    setHeight(h);
  }, []);

  // callback ref: runs as soon as React attaches the node (before effects)
  const ref = React.useCallback(
    (node: T | null) => {
      nodeRef.current = node;
      if (node) {
        // 1) measure immediately on attach
        measure(node);
        // 2) measure again on next frame (after layout/ResponsiveContainer)
        requestAnimationFrame(() => measure(node));
      }
    },
    [measure]
  );

  React.useLayoutEffect(() => {
    const el = nodeRef.current;
    if (!el) return;

    // 3) keep updated with ResizeObserver
    const ro = new ResizeObserver(() => measure(el));
    ro.observe(el);

    // 4) fonts can change metrics after first paint
    const fontReady = (document as any).fonts?.ready;
    let cancelled = false;
    if (fontReady && typeof fontReady.then === "function") {
      fontReady.then(() => {
        if (!cancelled && nodeRef.current) measure(nodeRef.current);
      });
    }

    // 5) safety: re-measure after load (some layouts settle late)
    const onLoad = () => nodeRef.current && measure(nodeRef.current);
    window.addEventListener("load", onLoad);

    return () => {
      cancelled = true;
      ro.disconnect();
      window.removeEventListener("load", onLoad);
    };
  }, [measure]);

  return { ref, width, height };
}
